import React, { useEffect, useMemo, useState } from 'react';
import type { ContactDto, FilePurpose } from '../../../shared/types';
import type { Ctx } from './ctx';
import { Icon } from './icons';
import { ErrorBanner, Field, Modal } from './ui';

/**
 * WhatsApp con un tasto (23/10/2026, Marco): apre la chat (WhatsApp per Windows se c'è, altrimenti WhatsApp Web)
 * con il messaggio già scritto. Per i documenti il programma copia il file: nella chat basta Ctrl+V e Invio.
 * Sul telefono si apre la scheda «Condividi» con il file già allegato.
 */

/** Numero nel formato di WhatsApp (solo cifre, con il prefisso del paese). null se non sembra un numero. */
export function waNumber(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let s = raw.split(/[,;/]| e /)[0].trim().replace(/[^\d+]/g, '');
  if (s.startsWith('+')) s = s.slice(1);
  else if (s.startsWith('00')) s = s.slice(2);
  else if (/^3\d{8,9}$/.test(s) || /^0\d{5,10}$/.test(s)) s = '39' + s;
  return /^\d{8,15}$/.test(s) ? s : null;
}

export interface WaFile {
  id: number;
  name: string;
  mime?: string;
  purpose?: FilePurpose | null;
}

export interface WaPerson {
  name: string;
  phone: string;
  note?: string;
}

/** Piccolo tasto verde: apre la finestra per scegliere a chi mandare il messaggio (e il documento). */
export function WhatsAppButton({
  ctx,
  file,
  to,
  prefer,
  advisor,
  label,
  className,
}: {
  ctx: Ctx;
  file?: WaFile;
  /** Destinatario già noto (es. il contatto della riga) */
  to?: WaPerson;
  /** Nome da mettere per primo nella lista (es. il consulente della pratica) */
  prefer?: string | null;
  /** Consulente della pratica: avvisa se un suo documento sta andando a qualcun altro */
  advisor?: string | null;
  label?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={className ?? 'btn ghost wa-btn'}
        title={file ? 'Manda questo documento su WhatsApp' : 'Scrivi su WhatsApp'}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <Icon name="whatsapp" size={15} />
        {label && <span>{label}</span>}
      </button>
      {open && <WhatsAppModal ctx={ctx} file={file} to={to} prefer={prefer} advisor={advisor} onClose={() => setOpen(false)} />}
    </>
  );
}

let contactsCache: { at: number; list: ContactDto[] } | null = null;

export function WhatsAppModal({
  ctx,
  file,
  to,
  prefer,
  advisor,
  onClose,
}: {
  ctx: Ctx;
  file?: WaFile;
  to?: WaPerson;
  prefer?: string | null;
  advisor?: string | null;
  onClose: () => void;
}) {
  const [contacts, setContacts] = useState<ContactDto[]>(contactsCache?.list ?? []);
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<WaPerson | null>(to ?? null);
  const [other, setOther] = useState('');
  const [text, setText] = useState(file ? `Buongiorno, ecco il documento: ${file.name.replace(/\.[a-z0-9]{2,4}$/i, '')}.` : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ mode: string; copied: boolean; who: string } | null>(null);

  useEffect(() => {
    if (contactsCache && Date.now() - contactsCache.at < 60_000) return;
    ctx.api
      .get<ContactDto[]>('/api/contacts')
      .then((l) => {
        contactsCache = { at: Date.now(), list: l };
        setContacts(l);
      })
      .catch(() => undefined);
  }, [ctx.api]);

  const people = useMemo(() => {
    const s = search.trim().toLowerCase();
    const pref = (prefer ?? '').trim().toLowerCase();
    const list: WaPerson[] = contacts
      .filter((c) => waNumber(c.phone))
      .filter((c) => !s || `${c.name} ${c.company ?? ''} ${c.phone}`.toLowerCase().includes(s))
      .map((c) => ({ name: c.name, phone: c.phone!, note: c.company ?? undefined }));
    if (pref) list.sort((a, b) => Number(b.name.toLowerCase().includes(pref)) - Number(a.name.toLowerCase().includes(pref)));
    // Preseleziona il consulente se c'è in rubrica con il numero
    return list.slice(0, 8);
  }, [contacts, search, prefer]);

  useEffect(() => {
    if (chosen || !prefer) return;
    const p = people.find((x) => x.name.toLowerCase().includes(prefer.toLowerCase()));
    if (p) setChosen(p);
  }, [people, prefer, chosen]);

  const target: WaPerson | null = other.trim() ? { name: other.trim(), phone: other.trim() } : chosen;
  const number = waNumber(target?.phone);

  // Avvisi per non mandare il documento sbagliato alla persona sbagliata
  const warning = (() => {
    if (!file || !target) return null;
    if (file.purpose === 'old') return 'È una VECCHIA VERSIONE: di solito non va mandata a nessuno.';
    if (file.purpose === 'internal') return 'Questo documento è segnato «Solo per noi».';
    if (file.purpose === 'advisor' && advisor && !target.name.toLowerCase().includes(advisor.toLowerCase()))
      return `Questo documento è per ${advisor} (versione per il Ministero): non mandarlo al cliente.`;
    if (file.purpose === 'client' && advisor && target.name.toLowerCase().includes(advisor.toLowerCase()))
      return `Questo è il documento per il cliente, non quello per ${advisor}.`;
    return null;
  })();

  const send = async () => {
    if (!number) return setError('Scegli una persona con il numero, oppure scrivi un numero valido (es. +39 333 1234567).');
    setBusy(true);
    setError(null);
    try {
      let payload: { name: string; mime: string; data: Uint8Array } | undefined;
      if (file) {
        const blob = await ctx.api.download(`/api/files/${file.id}/download`);
        payload = { name: file.name, mime: file.mime || blob.type || 'application/octet-stream', data: new Uint8Array(await blob.arrayBuffer()) };
      }
      const r = await window.tt.whatsapp(number, text.trim(), payload);
      setDone({ ...r, who: target!.name });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      narrow
      title={file ? 'Manda su WhatsApp' : 'Scrivi su WhatsApp'}
      onClose={onClose}
      footer={
        done ? (
          <button type="button" className="btn dark" onClick={onClose}>
            Fatto
          </button>
        ) : (
          <>
            <button type="button" className="btn" onClick={onClose} disabled={busy}>
              Annulla
            </button>
            <button type="button" className="btn good" onClick={() => void send()} disabled={busy || !number}>
              <Icon name="whatsapp" size={15} /> {busy ? 'Apro…' : 'Apri WhatsApp'}
            </button>
          </>
        )
      }
    >
      {done ? (
        <div className="wa-done">
          <p>
            Si è aperta la chat con <b>{done.who}</b>
            {done.mode === 'web' ? ' su WhatsApp Web' : done.mode === 'desktop' ? ' su WhatsApp' : ''}.
          </p>
          {file && done.copied && (
            <ol className="wa-steps">
              {text.trim() && <li>Premi <b>Invio</b> per mandare il messaggio.</li>}
              <li>
                Il documento è già copiato: clicca nella chat e premi <b>Ctrl+V</b>.
              </li>
              <li>
                Premi <b>Invio</b> per mandarlo.
              </li>
            </ol>
          )}
          {file && done.mode === 'downloaded' && (
            <p className="muted small">Il documento è stato scaricato: trascinalo nella chat di WhatsApp.</p>
          )}
          {file && done.mode === 'shown' && <p className="muted small">Ti ho aperto la cartella con il documento: trascinalo nella chat.</p>}
          {!file && <p className="muted small">Controlla il messaggio e premi Invio.</p>}
        </div>
      ) : (
        <>
          <ErrorBanner message={error} onClose={() => setError(null)} />
          {file && (
            <div className="wa-file small">
              <Icon name="paperclip" size={14} /> {file.name}
            </div>
          )}
          {to ? (
            <div className="wa-to">
              A: <b>{to.name}</b> <span className="muted">· {to.phone}</span>
            </div>
          ) : (
            <Field label="A chi">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca in rubrica…" />
              <div className="wa-people">
                {people.length === 0 && <div className="muted small">Nessuno in rubrica con il numero di telefono.</div>}
                {people.map((p) => (
                  <label key={p.name + p.phone} className={`wa-person${chosen?.phone === p.phone && !other.trim() ? ' on' : ''}`}>
                    <input
                      type="radio"
                      name="wa-to"
                      checked={chosen?.phone === p.phone && !other.trim()}
                      onChange={() => {
                        setChosen(p);
                        setOther('');
                      }}
                    />
                    <span>
                      <b>{p.name}</b>
                      <span className="muted small">
                        {p.note ? ` · ${p.note}` : ''} · {p.phone}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </Field>
          )}
          {!to && (
            <Field label="Oppure un altro numero">
              <input value={other} onChange={(e) => setOther(e.target.value)} inputMode="tel" placeholder="+39 333 1234567" />
            </Field>
          )}
          <Field label="Messaggio">
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Facoltativo" />
          </Field>
          {warning && <div className="wa-warn small">{warning}</div>}
        </>
      )}
    </Modal>
  );
}
